/**
 * UserList Component
 *
 * Administrative interface for viewing and managing user accounts.
 * Provides comprehensive list view with filtering, sorting, and account management.
 */

import React, { useState, useEffect, useMemo, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import type { ColDef, ICellRendererParams } from 'ag-grid-community';

import { AdminLayout } from '@/components/shared/layouts';
import {
  Card,
  CardHeader,
  CardBody,
  Button,
  Chip,
  Spinner,
  Select,
  SelectItem,
  Drawer,
  DrawerContent,
  DrawerHeader,
  DrawerBody,
  useDisclosure,
  useToastNotification
} from '@enfomentis/ui';
import { AGGridTable } from '@enfomentis/ui';
import { userService } from '@/services/UserService';
import { Brand } from '@enfomentis/shared/config';
import type {
  User,
  UserStatus,
  UserInvitationStats
} from '@/types/supabase/User';
import { RefreshCw, Filter, BarChart3, Mail, KeyRound } from 'lucide-react';
import { UserEmailManagementModal } from './UserEmailManagementModal';

/**
 * Props for UserList component
 */
export interface UserListProps {
  /** Optional title override */
  title?: string;
}

/**
 * Status filter options
 */
const STATUS_FILTER_OPTIONS = [
  { key: 'all', label: 'All Users' },
  { key: 'invited', label: 'Invited' },
  { key: 'active', label: 'Active' },
  { key: 'disabled', label: 'Disabled' }
] as const;

/**
 * User List Component
 *
 * Features:
 * - Professional data grid with AG Grid
 * - Real-time data from UserService
 * - Status filtering and indicators
 * - Responsive design for admin interface
 * - Loading states and error handling
 * - Navigation to detailed user management
 * - Account status management capabilities
 *
 * Usage:
 * ```tsx
 * <UserList title="User Management" />
 * ```
 */
export const UserList: React.FC<UserListProps> = ({
  title = "User Management"
}) => {
  const navigate = useNavigate();

  // State management
  const [users, setUsers] = useState<User[]>([]);
  const [loading, setLoading] = useState<boolean>(true);
  const [error, setError] = useState<string | null>(null);
  const [statusFilter, setStatusFilter] = useState<string>('all');
  const [invitationStats, setInvitationStats] = useState<UserInvitationStats | null>(null);
  const [passwordResetLoading, setPasswordResetLoading] = useState<Set<string>>(new Set());

  // Modal for mobile stats
  const { isOpen, onOpen, onClose } = useDisclosure();

  // Email management modal
  const { isOpen: isEmailManagementModalOpen, onOpen: openEmailManagementModal, onClose: closeEmailManagementModal } = useDisclosure();

  // Toast notifications
  const { showToast } = useToastNotification();

  // Load data
  const loadUsers = async (): Promise<void> => {
    try {
      setLoading(true);
      setError(null);

      const [usersData, statsData] = await Promise.all([
        userService.getAll(),
        userService.getInvitationStats()
      ]);

      setUsers(usersData);
      setInvitationStats(statsData);
    } catch (err) {
      console.error('Failed to load users:', err);
      setError('Failed to load users. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  // Load data on mount
  useEffect(() => {
    loadUsers();
  }, []);

  // Handle password reset for individual user
  const handlePasswordReset = useCallback(async (user: User): Promise<void> => {
    if (user.status !== 'active') {
      showToast({
        color: 'warning',
        title: 'Cannot Send Password Reset',
        description: 'Password reset can only be sent to active users.'
      });
      return;
    }

    try {
      // Add user to loading set
      setPasswordResetLoading(prev => new Set(prev).add(user.id));

      const result = await userService.sendPasswordReset(user.id, 'admin');

      showToast({
        color: 'success',
        title: 'Password Reset Sent',
        description: `Password reset email sent to ${user.email}. The user will receive instructions to reset their password.`
      });

      console.log('✅ Password reset sent successfully:', result);
    } catch (err) {
      console.error('Failed to send password reset:', err);
      const errorMessage = err instanceof Error ? err.message : 'Failed to send password reset';

      showToast({
        color: 'danger',
        title: 'Password Reset Failed',
        description: `Failed to send password reset to ${user.email}. ${errorMessage}`
      });
    } finally {
      // Remove user from loading set
      setPasswordResetLoading(prev => {
        const newSet = new Set(prev);
        newSet.delete(user.id);
        return newSet;
      });
    }
  }, [showToast]);

  // Filter users based on status
  const filteredUsers = useMemo(() => {
    if (statusFilter === 'all') {
      return users;
    }
    return users.filter(user => user.status === statusFilter);
  }, [users, statusFilter]);

  // Status chip component
  const StatusBadge: React.FC<{ status: UserStatus }> = ({ status }) => {
    const variants: Record<UserStatus, { color: 'warning' | 'success' | 'danger' | 'primary'; text: string }> = {
      active: { color: 'success', text: 'Active' },
      disabled: { color: 'danger', text: 'Disabled' },
      pending_payment: { color: 'warning', text: 'Pending Payment' }
    };

    const variant = variants[status];
    return (
      <Chip color={variant.color} variant="flat" size="sm">
        {variant.text}
      </Chip>
    );
  };

  // Detect if we're on mobile and fullscreen mode
  const [isMobile, setIsMobile] = useState(window.innerWidth < 768);
  const [isFullScreen, setIsFullScreen] = useState(() => {
    const stored = localStorage.getItem(`${Brand.cachePrefix}-fullscreen`);
    return stored ? JSON.parse(stored) : false;
  });

  useEffect(() => {
    const handleResize = () => {
      setIsMobile(window.innerWidth < 768);
    };

    const handleStorageChange = () => {
      const stored = localStorage.getItem(`${Brand.cachePrefix}-fullscreen`);
      setIsFullScreen(stored ? JSON.parse(stored) : false);
    };

    window.addEventListener('resize', handleResize);
    window.addEventListener('storage', handleStorageChange);

    // Also listen for direct localStorage changes in same tab
    const originalSetItem = localStorage.setItem;
    localStorage.setItem = function(key, value) {
      originalSetItem.apply(this, [key, value]);
      if (key === `${Brand.cachePrefix}-fullscreen`) {
        handleStorageChange();
      }
    };

    return () => {
      window.removeEventListener('resize', handleResize);
      window.removeEventListener('storage', handleStorageChange);
      localStorage.setItem = originalSetItem;
    };
  }, []);

  // Format date helper
  const formatDate = (dateString: string | null): string => {
    if (!dateString) return '—';
    return new Date(dateString).toLocaleDateString('en-US', {
      month: 'short',
      day: 'numeric',
      year: 'numeric'
    });
  };

  // AG Grid column definitions - conditional based on mobile/desktop
  const columnDefs: ColDef<User>[] = useMemo(() => [
    {
      field: 'status',
      headerName: 'Status',
      width: 120,
      cellRenderer: (params: ICellRendererParams<User>) => <StatusBadge status={params.value} />,
      filter: !isMobile,
      sortable: true,
      resizable: !isMobile
    },
    {
      field: 'fullName',
      headerName: 'Full Name',
      width: 200,
      filter: !isMobile,
      sortable: true,
      resizable: !isMobile
    },
    {
      field: 'email',
      headerName: 'Email',
      width: 250,
      filter: !isMobile,
      sortable: true,
      resizable: !isMobile
    },
    {
      field: 'createdAt',
      headerName: 'Account Created',
      width: 150,
      cellRenderer: (params: ICellRendererParams<User>) => {
        const date = new Date(params.value);
        return date.toLocaleDateString();
      },
      filter: !isMobile ? 'agDateColumnFilter' : false,
      sortable: true,
      resizable: !isMobile
    },
    {
      field: 'invitedAt',
      headerName: 'Invited',
      width: 150,
      cellRenderer: (params: ICellRendererParams<User>) => formatDate(params.value),
      filter: !isMobile ? 'agDateColumnFilter' : false,
      sortable: true,
      resizable: !isMobile
    },
    {
      field: 'activatedAt',
      headerName: 'Activated',
      width: 150,
      cellRenderer: (params: ICellRendererParams<User>) => formatDate(params.value),
      filter: !isMobile ? 'agDateColumnFilter' : false,
      sortable: true,
      resizable: !isMobile
    },
    {
      field: 'onboardingCompleted',
      headerName: 'Onboarding',
      width: 130,
      cellRenderer: (params: ICellRendererParams<User>) => {
        const completed = params.value;
        return (
          <Chip
            size="sm"
            variant="flat"
            color={completed ? 'success' : 'default'}
          >
            {completed ? 'Complete' : 'Pending'}
          </Chip>
        );
      },
      filter: !isMobile,
      sortable: true,
      resizable: !isMobile
    },
    // Only show Pwd Reset column on desktop
    ...(!isMobile ? [{
      colId: 'password-reset',
      headerName: 'Pwd Reset',
      width: 110,
      cellRenderer: (params: ICellRendererParams<User>) => {
        const user = params.data;
        if (!user) return null;

        const isLoading = passwordResetLoading.has(user.id);
        const isActive = user.status === 'active';

        return (
          <Button
            size="sm"
            variant="flat"
            color={isActive ? 'warning' : 'default'}
            onPress={() => handlePasswordReset(user)}
            isLoading={isLoading}
            isDisabled={!isActive || isLoading}
            className="my-1"
          >
            <div className="flex items-center gap-1">
              <KeyRound size={14} />
              <span>Reset</span>
            </div>
          </Button>
        );
      },
      filter: false,
      sortable: false,
      resizable: false,
      pinned: 'right' as const
    }] : []),
    {
      colId: 'details',
      headerName: 'Details',
      width: 100,
      cellRenderer: (params: ICellRendererParams<User>) => (
        <Button
          size="sm"
          variant="bordered"
          color="primary"
          onPress={() => navigate(`/admin/users/${params.data?.id}`)}
          className="my-1"
        >
          View
        </Button>
      ),
      filter: false,
      sortable: false,
      resizable: false,
      pinned: 'left'
    }
  ], [isMobile, navigate, handlePasswordReset, passwordResetLoading]);

  // Grid options - conditional based on mobile/desktop
  const gridOptions = {
    defaultColDef: {
      resizable: !isMobile,
      sortable: true,
      filter: !isMobile
    },
    rowSelection: {
      mode: 'singleRow',
      checkboxes: false,
      enableClickSelection: true
    } as const,
    pagination: true,
    paginationPageSize: isMobile ? 25 : 50,
    paginationPageSizeSelector: isMobile ? false : [25, 50, 100],
    animateRows: true,
    enableCellTextSelection: true,
    suppressMenuHide: isMobile
  };

  return (
    <AdminLayout title={title}>
      <Card className="space-y-6" radius="none" shadow="none">
        {/* Overview Cards - Hidden on mobile */}
        {invitationStats && (
          <CardHeader className="hidden md:grid grid-cols-2 md:grid-cols-4 gap-4">
            <Card shadow="sm">
              <CardBody className="text-center py-4">
                <p className="text-2xl font-bold text-success">{invitationStats.activeUsers}</p>
                <p className="text-sm text-text-secondary">Active</p>
              </CardBody>
            </Card>
            <Card shadow="sm">
              <CardBody className="text-center py-4">
                <p className="text-2xl font-bold text-warning">{invitationStats.pendingInvitations}</p>
                <p className="text-sm text-text-secondary">Invited</p>
              </CardBody>
            </Card>
            <Card shadow="sm">
              <CardBody className="text-center py-4">
                <p className="text-2xl font-bold text-danger">{invitationStats.disabledUsers}</p>
                <p className="text-sm text-text-secondary">Disabled</p>
              </CardBody>
            </Card>
            <Card shadow="sm">
              <CardBody className="text-center py-4">
                <p className="text-2xl font-bold text-primary">{invitationStats.onboardingCompletionRate}%</p>
                <p className="text-sm text-text-secondary">Onboarded</p>
              </CardBody>
            </Card>
          </CardHeader>
        )}

        <CardBody className="space-y-6">
          {/* Main Users Table */}
          <Card shadow="none" radius="none" className='flex-shrink-0'>
            <CardHeader>
              <div className="flex items-center justify-between w-full">
                {/* Desktop filter */}
                <div className="hidden md:flex items-center gap-2">
                  <Filter size={16} className="text-text-secondary" />
                  <Select
                    size="sm"
                    selectedKeys={[statusFilter]}
                    onSelectionChange={(keys) => {
                      const selectedKey = Array.from(keys)[0] as string;
                      setStatusFilter(selectedKey || 'all');
                    }}
                    className="min-w-[180px]"
                    aria-label="Filter by status"
                  >
                    {STATUS_FILTER_OPTIONS.map((option) => (
                      <SelectItem key={option.key}>
                        {option.label}
                      </SelectItem>
                    ))}
                  </Select>
                </div>

                <div className="flex items-center gap-2 w-full md:w-auto">
                  {/* Mobile filter - simplified */}
                  <div className="md:hidden">
                    <Select
                      size="sm"
                      selectedKeys={[statusFilter]}
                      onSelectionChange={(keys) => {
                        const selectedKey = Array.from(keys)[0] as string;
                        setStatusFilter(selectedKey || 'all');
                      }}
                      className="min-w-[140px]"
                      aria-label="Filter by status"
                    >
                      {STATUS_FILTER_OPTIONS.map((option) => (
                        <SelectItem key={option.key}>
                          {option.label}
                        </SelectItem>
                      ))}
                    </Select>
                  </div>
                  <div className="flex-1 md:flex-none"></div>
                  <Button
                    size="sm"
                    variant="flat"
                    color="secondary"
                    onPress={openEmailManagementModal}
                    className="sm:min-w-0"
                  >
                    <div className="flex items-center gap-2">
                      <Mail size={16} />
                      <span className="hidden sm:inline">Email Management</span>
                    </div>
                  </Button>
                  <Button
                    size="sm"
                    variant="flat"
                    color="primary"
                    onPress={loadUsers}
                    isLoading={loading}
                    className="sm:min-w-0"
                  >
                    <div className="flex items-center gap-2">
                      <RefreshCw size={16} />
                      <span className="hidden sm:inline">Refresh</span>
                    </div>
                  </Button>
                </div>
              </div>
            </CardHeader>
            <CardBody className="p-0">
              {error && (
                <div className="p-6 text-center">
                  <p className="text-danger">{error}</p>
                  <Button
                    size="sm"
                    color="primary"
                    onPress={loadUsers}
                    className="mt-4"
                  >
                    Retry
                  </Button>
                </div>
              )}

              {loading && !error && (
                <div className="flex items-center justify-center py-12">
                  <Spinner color="primary" />
                  <span className="ml-3 text-text-secondary">Loading users...</span>
                </div>
              )}

              {!loading && !error && (
                <div className={`min-h-[200px] ${
                  isFullScreen
                    ? isMobile
                      ? "h-[calc(100vh-325px)]" // Fullscreen mobile
                      : "h-[calc(100vh-465px)]" // Fullscreen desktop
                    : isMobile
                      ? "h-[calc(100vh-375px)]" // Normal mobile with header showing
                      : "md:h-[480px]" // Normal desktop
                }`}>
                  <AGGridTable
                    rowData={filteredUsers}
                    columnDefs={columnDefs}
                    gridOptions={gridOptions}
                    height="100%"
                  />
                </div>
              )}
            </CardBody>
          </Card>

          {/* Mobile Floating Stats Button */}
          {invitationStats && (
            <Button
              isIconOnly
              color="primary"
              variant="shadow"
              size="lg"
              onPress={onOpen}
              className="md:hidden fixed bottom-6 left-6 z-50 shadow-lg"
              aria-label="View user statistics"
            >
              <BarChart3 size={24} />
            </Button>
          )}

          {/* Mobile Stats Drawer */}
          {invitationStats && (
            <Drawer isOpen={isOpen} onClose={onClose} placement="bottom">
              <DrawerContent>
                <DrawerHeader>
                  <h3 className="text-lg font-semibold">User Statistics</h3>
                </DrawerHeader>
                <DrawerBody>
                  <div className="grid grid-cols-2 gap-4 pb-4">
                    <div className="text-center p-4 bg-success-50 dark:bg-success-900/20 rounded-lg">
                      <p className="text-2xl font-bold text-success">{invitationStats.activeUsers}</p>
                      <p className="text-sm text-text-secondary">Active</p>
                    </div>
                    <div className="text-center p-4 bg-warning-50 dark:bg-warning-900/20 rounded-lg">
                      <p className="text-2xl font-bold text-warning">{invitationStats.pendingInvitations}</p>
                      <p className="text-sm text-text-secondary">Invited</p>
                    </div>
                    <div className="text-center p-4 bg-danger-50 dark:bg-danger-900/20 rounded-lg">
                      <p className="text-2xl font-bold text-danger">{invitationStats.disabledUsers}</p>
                      <p className="text-sm text-text-secondary">Disabled</p>
                    </div>
                    <div className="text-center p-4 bg-primary-50 dark:bg-primary-900/20 rounded-lg">
                      <p className="text-2xl font-bold text-primary">{invitationStats.onboardingCompletionRate}%</p>
                      <p className="text-sm text-text-secondary">Onboarded</p>
                    </div>
                  </div>
                </DrawerBody>
              </DrawerContent>
            </Drawer>
          )}
        </CardBody>
      </Card>

      {/* Email Management Modal */}
      <UserEmailManagementModal
        isOpen={isEmailManagementModalOpen}
        onClose={closeEmailManagementModal}
      />
    </AdminLayout>
  );
};